import React, { useState } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

const FREQ_LABELS = {
  one_time: 'One-time',
  monthly: 'Monthly',
  annual: 'Annual',
  semi_annual: 'Semi-annual',
};

export default function PlannedExpenses({ data, onRefresh, defaultCollapsed }) {
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    amount: '',
    dueDate: '',
    frequency: 'one_time',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!data) return <PanelSkeleton />;

  const items = data;
  const unpaid = items.filter((i) => !i.is_paid || i.frequency === 'monthly');
  const paid = items.filter((i) => i.is_paid && i.frequency !== 'monthly');

  const annualizedTotal = unpaid.reduce((sum, i) => {
    const amt = parseFloat(i.amount);
    if (i.frequency === 'semi_annual') return sum + amt * 2;
    if (i.frequency === 'monthly') return sum + amt * 12;
    return sum + amt;
  }, 0);

  const togglePaid = async (id) => {
    setBusyId(id);
    try {
      await api.togglePlannedExpensePaid(id);
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const confirmMatch = async (id, transactionId) => {
    setBusyId(id);
    try {
      await api.confirmPlannedExpenseMatch(id, transactionId);
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const dismissMatch = async (id, transactionId) => {
    setBusyId(id);
    try {
      await api.dismissPlannedExpenseMatch(id, transactionId);
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this planned expense?')) return;
    setBusyId(id);
    try {
      await api.deletePlannedExpense(id);
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.amount || !form.dueDate) {
      setError('Name, amount, and due date are required.');
      return;
    }

    setSubmitting(true);
    try {
      await api.addPlannedExpense({
        name: form.name.trim(),
        amount: parseFloat(form.amount),
        dueDate: form.dueDate,
        frequency: form.frequency,
      });
      setForm({ name: '', amount: '', dueDate: '', frequency: 'one_time' });
      setShowForm(false);
      onRefresh();
    } catch (err) {
      setError('Failed to add. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CollapsiblePanel
      title="📌 Planned Expenses"
      defaultCollapsed={defaultCollapsed}
      headerExtra={
        <button style={styles.addBtn} onClick={(e) => { e.stopPropagation(); setShowForm((s) => !s); }}>
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      }
    >
      {annualizedTotal > 0 && (
        <p style={styles.note}>
          Reserving <strong>{fmt.currency(annualizedTotal)}</strong>/yr from your
          budget for unpaid planned expenses.
        </p>
      )}

      {showForm && (
        <form style={styles.form} onSubmit={submit}>
          <input
            style={styles.input}
            placeholder="Name (e.g. Property Tax)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div style={styles.formRow}>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
            <input
              style={styles.input}
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />
          </div>
          <select
            style={styles.input}
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
          >
            <option value="one_time">One-time</option>
            <option value="monthly">Monthly (every month)</option>
            <option value="semi_annual">Semi-annual (twice a year)</option>
            <option value="annual">Annual (once a year)</option>
          </select>
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.submitBtn} type="submit" disabled={submitting}>
            {submitting ? 'Adding...' : 'Add Planned Expense'}
          </button>
        </form>
      )}

      {items.length === 0 && !showForm ? (
        <p style={styles.empty}>
          No planned expenses yet. Add big irregular costs like property tax
          or a vacation so they're factored into your budget.
        </p>
      ) : (
        <>
          {unpaid.map((item) => (
            <PlannedRow
              key={item.id}
              item={item}
              busy={busyId === item.id}
              onToggle={() => togglePaid(item.id)}
              onDelete={() => remove(item.id)}
              onConfirmMatch={() => confirmMatch(item.id, item.candidate_txn_id)}
              onDismissMatch={() => dismissMatch(item.id, item.candidate_txn_id)}
            />
          ))}
          {paid.length > 0 && (
            <>
              <div style={styles.paidDivider}>Paid</div>
              {paid.map((item) => (
                <PlannedRow
                  key={item.id}
                  item={item}
                  busy={busyId === item.id}
                  onToggle={() => togglePaid(item.id)}
                  onDelete={() => remove(item.id)}
                />
              ))}
            </>
          )}
        </>
      )}
    </CollapsiblePanel>
  );
}

function PlannedRow({ item, busy, onToggle, onDelete, onConfirmMatch, onDismissMatch }) {
  const isMonthly = item.frequency === 'monthly';
  const hasMatch = !item.is_paid && item.candidate_txn_id;

  return (
    <div>
      <div style={{
        ...styles.row,
        opacity: item.is_paid && !isMonthly ? 0.5 : 1,
        marginBottom: hasMatch ? 0 : undefined,
        borderRadius: hasMatch ? '8px 8px 0 0' : undefined,
      }}>
        <div style={styles.rowLeft}>
          {isMonthly ? (
            <div style={styles.recurringBadge} title="Recurring - always counted in your budget">
              ↻
            </div>
          ) : (
            <button
              style={{
                ...styles.checkbox,
                ...(item.is_paid ? styles.checkboxChecked : {}),
              }}
              onClick={onToggle}
              disabled={busy}
              aria-label={item.is_paid ? 'Mark unpaid' : 'Mark paid'}
            >
              {item.is_paid ? '✓' : ''}
            </button>
          )}
          <div>
            <div style={{ ...styles.rowName, ...(item.is_paid && !isMonthly ? styles.strike : {}) }}>
              {item.name}
            </div>
            <div style={styles.rowMeta}>
              {isMonthly ? 'Every month' : fmt.date(item.due_date)} · {FREQ_LABELS[item.frequency]}
            </div>
          </div>
        </div>
        <div style={styles.rowRight}>
          <div style={styles.rowAmt}>{fmt.currency(item.amount)}</div>
          <button style={styles.deleteBtn} onClick={onDelete} disabled={busy}>
            ✕
          </button>
        </div>
      </div>

      {hasMatch && (
        <div style={styles.matchPrompt}>
          <span style={styles.matchText}>
            Possible match: <strong>{item.candidate_merchant || item.candidate_description}</strong>{' '}
            {fmt.currency(item.candidate_amount)} on {fmt.date(item.candidate_date)}
          </span>
          <div style={styles.matchBtns}>
            <button style={styles.matchConfirmBtn} onClick={onConfirmMatch} disabled={busy}>
              Match
            </button>
            <button style={styles.matchDismissBtn} onClick={onDismissMatch} disabled={busy}>
              Not this one
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div style={styles.panel}>
      <div style={{ height: 20, width: '50%', background: '#1e2a3a', borderRadius: 6 }} />
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  addBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  note: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    margin: '0 0 1rem',
    fontFamily: "'Syne', sans-serif",
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1rem',
    padding: '1rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
  },
  formRow: { display: 'flex', gap: '0.5rem' },
  input: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.5rem 0.6rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    flex: 1,
    width: '100%',
  },
  error: {
    color: '#ef4444',
    fontSize: '0.75rem',
    margin: 0,
  },
  submitBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.65rem',
    fontSize: '0.82rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: '1rem 0',
    margin: 0,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    marginBottom: '0.4rem',
    border: '1px solid #1e2a3a',
  },
  rowLeft: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  matchPrompt: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: 'rgba(251, 191, 36, 0.08)',
    border: '1px solid #fbbf24',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    marginBottom: '0.4rem',
    flexWrap: 'wrap',
  },
  matchText: {
    color: '#d1d5db',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  matchBtns: { display: 'flex', gap: '0.4rem', flexShrink: 0 },
  matchConfirmBtn: {
    background: '#fbbf24',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  matchDismissBtn: {
    background: 'transparent',
    color: '#9ca3af',
    border: '1px solid #374151',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  recurringBadge: {
    width: '22px',
    height: '22px',
    minWidth: '22px',
    borderRadius: '6px',
    border: '1px solid #374151',
    background: 'rgba(0, 212, 170, 0.08)',
    color: '#00d4aa',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkbox: {
    width: '22px',
    height: '22px',
    minWidth: '22px',
    borderRadius: '6px',
    border: '1px solid #374151',
    background: 'transparent',
    color: '#00d4aa',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    background: 'rgba(0, 212, 170, 0.15)',
    borderColor: '#00d4aa',
  },
  rowName: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
  },
  strike: { textDecoration: 'line-through' },
  rowMeta: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
    marginTop: '2px',
  },
  rowRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  rowAmt: {
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
    color: '#fff',
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    cursor: 'pointer',
    fontSize: '0.9rem',
    padding: '0 4px',
  },
  paidDivider: {
    color: '#4b5563',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0.75rem 0 0.5rem',
    fontFamily: "'Syne', sans-serif",
  },
};
